import React, { FC, useState, useRef, useEffect } from 'react';

interface WritingAreaProps {
  isActive: boolean;
  onPause: () => void;
  onResumeTyping: () => void;
  pauseThreshold: number;
  onComplete: (text: string) => void;
  wasTextDeleted: boolean;
  setWasTextDeleted: (value: boolean) => void;
  title: string;
  goal: string;
  rules: {
    bannedWords: string[];
    requiredWords: string[];
    wordLengthPattern: number[] | null;
    shouldRhyme: boolean;
  };
}

const WritingArea: FC<WritingAreaProps> = ({
  isActive,
  onPause,
  onResumeTyping,
  pauseThreshold,
  onComplete,
  wasTextDeleted,
  setWasTextDeleted,
  title,
  goal,
  rules
}) => {
  const [text, setText] = useState<string>('');
  const [charCount, setCharCount] = useState<number>(0);
  const [wordCount, setWordCount] = useState<number>(0);
  const [ruleViolations, setRuleViolations] = useState<string[]>([]);
  
  // Use a ref to track the pause timer
  const pauseTimerRef = useRef<NodeJS.Timeout | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const completedRef = useRef<boolean>(false);
  const prevActiveRef = useRef<boolean>(isActive);

  // Focus the textarea when the writing session starts
  useEffect(() => {
    if (isActive && textAreaRef.current) {
      textAreaRef.current.focus();
      completedRef.current = false;
    }
    
    // When transitioning from active to inactive (writing ends), clear the pause timer
    if (prevActiveRef.current && !isActive) {
      if (pauseTimerRef.current) {
        clearTimeout(pauseTimerRef.current);
        pauseTimerRef.current = null;
      }
    }
    
    prevActiveRef.current = isActive;
  }, [isActive]);

  // Effect to clear text if wasTextDeleted is true (triggered by pausing too long)
  useEffect(() => {
    if (wasTextDeleted) {
      // Show the deletion message
      setText(`YOUR TEXT WAS DELETED BECAUSE YOU STOPPED TYPING FOR MORE THAN ${pauseThreshold} SECONDS!`);
      
      // Then, after a short delay, clear the text completely
      setTimeout(() => {
        setText('');
        setWasTextDeleted(false);
      }, 1500);
    }
  }, [wasTextDeleted, setWasTextDeleted, pauseThreshold]);

  // Calculate word and character count
  useEffect(() => {
    setCharCount(text.length);
    setWordCount(text.trim() === '' ? 0 : text.trim().split(/\s+/).length);
  }, [text]);

  // Submit text when the timer completes (handled by parent component)
  useEffect(() => {
    // Track when writing stops successfully (timer ends)
    if (!isActive && text.trim() !== '' && !wasTextDeleted && !completedRef.current) {
      completedRef.current = true; // Mark as completed first to prevent multiple calls
      onComplete(text);
    }
  }, [isActive, text, onComplete, wasTextDeleted]);

  // Clean up pause timer when component unmounts
  useEffect(() => {
    return () => {
      if (pauseTimerRef.current) {
        clearTimeout(pauseTimerRef.current);
        pauseTimerRef.current = null;
      }
    };
  }, []);

  // Reset text when a new writing session starts
  useEffect(() => {
    if (isActive) {
      setText('');
      setRuleViolations([]);
    }
  }, [isActive]);

  // Check for rule violations
  useEffect(() => {
    if (!isActive || text.trim() === '') {
      setRuleViolations([]);
      return;
    }

    const violations: string[] = [];
    const words = text.trim().toLowerCase().split(/\s+/);
    const lastWord = words[words.length - 1];
    
    // Check for banned words
    if (rules.bannedWords.length > 0) {
      const bannedWordsUsed = rules.bannedWords.filter(banned => 
        text.toLowerCase().includes(banned.toLowerCase())
      );
      
      if (bannedWordsUsed.length > 0) {
        violations.push(`Used banned words: ${bannedWordsUsed.join(', ')}`);
      }
    }
    
    // Check word length pattern
    if (rules.wordLengthPattern && rules.wordLengthPattern.length > 0) {
      const pattern = rules.wordLengthPattern;
      const invalidWords = words.filter((word, index) => {
        const expectedLength = pattern[index % pattern.length];
        return word.length !== expectedLength;
      });
      
      if (invalidWords.length > 0) {
        violations.push(`Some words don't follow the pattern: ${pattern.join('-')}`);
      }
    }
    
    // Check for rhyming (simplified example)
    if (rules.shouldRhyme && words.length > 5) {
      // This is a very basic rhyme check - in a real app, you'd use a rhyming dictionary API
      const sentences = text.split(/[.!?]+/).filter(s => s.trim());
      if (sentences.length >= 2) {
        violations.push('Remember to make your sentences rhyme!');
      }
    }
    
    setRuleViolations(violations);
  }, [text, rules, isActive]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!isActive) return;
    
    setText(e.target.value);
    
    // Clear any existing pause timer
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
    
    // Set a new pause timer
    pauseTimerRef.current = setTimeout(() => {
      // User has paused typing for longer than the threshold
      onPause();
      pauseTimerRef.current = null;
    }, pauseThreshold * 1000);
    
    // If this is the first keystroke after a pause, inform the parent
    onResumeTyping();
  };

  return (
    <div className="mb-6">
      <div className="relative">
        {/* Rules Reminder */}
        {isActive && rules && (
          <div className="mb-2 p-2 bg-indigo-50 rounded-md">
            <div className="flex flex-wrap gap-2">
              {rules.bannedWords.length > 0 && (
                <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                  Banned: {rules.bannedWords.join(', ')}
                </span>
              )}
              {rules.requiredWords.length > 0 && (
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                  Required: {rules.requiredWords.join(', ')}
                </span>
              )}
              {rules.wordLengthPattern && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  Pattern: {rules.wordLengthPattern.join('-')}
                </span>
              )}
              {rules.shouldRhyme && (
                <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                  Make it rhyme!
                </span>
              )}
            </div>
          </div>
        )}
        
        <textarea
          ref={textAreaRef}
          value={text}
          onChange={handleChange}
          disabled={!isActive || wasTextDeleted}
          placeholder={isActive ? `Start typing... Don't pause for more than ${pauseThreshold} seconds or your text will be deleted!` : "Press Start to begin writing..."}
          className={`w-full h-64 p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all ${
            wasTextDeleted ? 'bg-red-100 animate-pulse text-red-600 font-bold' : 'bg-white'
          } ${!isActive ? 'bg-gray-100' : ''}`}
        />
        
        <div className="absolute bottom-3 right-3 text-xs text-gray-500">
          {charCount} characters | {wordCount} words
        </div>
      </div>
      
      {/* Rule Violations */}
      {isActive && ruleViolations.length > 0 && (
        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
          <h4 className="text-sm font-medium text-yellow-800">Rule Violations:</h4>
          <ul className="mt-1 text-xs text-yellow-700 list-disc list-inside">
            {ruleViolations.map((violation, index) => (
              <li key={index}>{violation}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default WritingArea; 